<script lang="ts" setup>
interface ReservationOverlayProps {
    departureAgency?: string | undefined
    returnAgency?: string | undefined
    category?: string | undefined
    departureTime?: Date | undefined
    returnTime?: Date | undefined
}

const props = defineProps<ReservationOverlayProps>()
const { category: cat, departureAgency: depA, returnAgency: retA, departureTime: depT, returnTime: retT } = useReservationParams()

const _departureAgency = computed(() => props.departureAgency || depA.value)
const _returnAgency = computed(() => props.returnAgency || retA.value)
const _category = computed(() => props.category || cat.value)
const _departureTime = computed(() => props.departureTime || depT.value)
const _returnTime = computed(() => props.returnTime || retT.value)

const root = ref<HTMLDialogElement>()

const isOpen = defineModel<boolean>({ default: false })
const close = () => (isOpen.value = false)

watch(isOpen, (value) => {
    if (value) {
        root.value?.showModal?.()
    }
    else {
        root.value?.close?.()
    }
})

useEventListener(document, 'keyup', (event) => {
    if (event.key === 'Escape') {
        event.preventDefault()
        close()
    }
})

const $style = useCssModule()
const rootClasses = computed(() => {
    return [$style.root, isOpen.value && $style['root--open']]
})

// Can't use dialog element because inner component 'VReservationDatePicker' need to be above
</script>

<template>
    <div
        v-show="isOpen"
        ref="root"
        role="dialog"
        :class="rootClasses"
    >
        <div
            :class="$style.container"
            class="main-content-width--mobile-wider"
        >
            <VButton
                :class="$style.close"
                :aria-label="$t('close')"
                outlined
                size="s"
                icon-name="close-small"
                @click.prevent="close"
            />
            <VReservationForm
                :class="$style.form"
                :category="_category"
                :departure-agency="_departureAgency"
                :return-agency="_returnAgency"
                :departure-time="_departureTime"
                :return-time="_returnTime"
            />
        </div>
        <div
            :class="$style.backdrop"
            @click.prevent="close"
        />
    </div>
</template>

<style lang="scss" module>
.root {
    display: none;
    max-width: 100%;
    max-height: 100%;

    &--open {
        position: fixed;
        z-index: 101;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        border: none;
        gap: 8px;
        inset: 0;
    }
}

.backdrop {
    position: fixed;
    background-color: var(--color-surface-overlay);
    cursor: pointer;
    inset: 0;
}

.container {
    position: relative;
    z-index: 101;
    width: 100%;
    margin-top: $v-top-bar-s + rem(0);

    @include media('>=lg') {
        margin-top: rem(184);
    }
}

.close {
    position: absolute;
    top: rem(-18);
    right: 0;
    border: 1px solid var(--color-line-primary, rgb(1 1 1 / 15%));
    background-color: var(--color-white);
    translate: 0 -100%;
}

.form {
    max-width: initial;
    max-height: 90vh;
    overflow-y: scroll;

    &::-webkit-scrollbar {
        display: none;
    }
}
</style>
